import {
  AutocompleteWithLabel,
  CountrySelectorForHeader,
  InputWithLabel,
  TextAreaWithLabel
} from '@adotel/components'
import { useLazyQuery, useQuery } from '@apollo/client'
import { Typography } from '@material-ui/core'
import { FormikErrors, FormikTouched } from 'formik'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { PROVIDER_QUERY, PROVIDERS_QUERY } from '../../graphql/providerQueries'
import { countries, CountryType } from '../../helpers/countriesList'
import { getRoleFromStorage } from '../../helpers/userData'
import { getTranslate, lang } from '../../localization'
import cancelPolicyPagesRoleManagement from '../../staticRoleManagement/cancelPolicyRoleManagement'
import { CancelPolicy } from '../../types/cancelAndModifyPolicies'
import { Provider, ProviderBrief } from '../../types/provider'
import { Multilingual } from '../../types/roomtype'
import useStyles from './styles/PoliciesFormInputBox.styles'

interface PoliciesFormInputBoxProps {
  values: CancelPolicy
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void

  handleBlur: any
  isSubmitting: any
  errors: FormikErrors<CancelPolicy>
  touched: FormikTouched<CancelPolicy>
  descriptionMultiLingual: Multilingual[]
  setdescriptionMultiLingual: (newValue: Multilingual[]) => void
  loadingForData: boolean
  providerCodeState: string
  setproviderCodeState: React.Dispatch<React.SetStateAction<string>>
}
const PoliciesFormInputBox: React.FC<PoliciesFormInputBoxProps> = ({
  values,
  setFieldValue,
  errors,
  touched,
  handleBlur,
  isSubmitting,
  descriptionMultiLingual,
  setdescriptionMultiLingual,
  loadingForData,
  providerCodeState,
  setproviderCodeState
}) => {
  const { providerCode, id } = useParams<{
    providerCode: string
    id: string
  }>()
  const { data: providers, loading: loadingForProvidersList } = useQuery<{
    providers: ProviderBrief[]
  }>(PROVIDERS_QUERY)

  const [getProvider, { data: singleProviderData }] = useLazyQuery<{
    provider: Provider
  }>(PROVIDER_QUERY)

  useEffect(() => {
    if (id) {
      getProvider({
        variables: {
          providerCode: providerCode
        }
      })
    }
  }, [])
  const [selectedCountryForName, setselectedCountryForName] =
    useState<CountryType>(countries[0])
  const nameLanguage =
    selectedCountryForName?.nativeLanguage && lang !== 'en'
      ? selectedCountryForName.nativeLanguage
      : selectedCountryForName?.language

  const classes = useStyles()
  const [autoCompleteLabelForProvider, setAutoCompleteLabelForProvider] =
    useState<any>({})

  useEffect(() => {
    if (!singleProviderData?.provider) return

    const value = singleProviderData.provider.providerCode
    const label = `${singleProviderData.provider.name} - ${singleProviderData.provider.displayProviderCode}`

    setAutoCompleteLabelForProvider({ value, label })
    setproviderCodeState(value)
  }, [singleProviderData])

  const hasAccessToChangeProvider = cancelPolicyPagesRoleManagement
    .find(
      (item) => item.route === '/policies/(cancel-policies|amendment-policies)'
    )
    ?.sections?.PROVIDER_SELECTOR?.rolesListWithWriteAccess?.includes(
      getRoleFromStorage()
    )

  return (
    <div>
      <div className={classes.textEditorContainer}>
        <div className={classes.input}>
          <InputWithLabel
            name="basicDetails.name"
            label={getTranslate('Name')}
            fullWidth
            placeholder={getTranslate('Choose a Name for the Policy')}
            inputSize="small"
            value={values.basicDetails.name}
            onChange={(event: { currentTarget: { value: any } }) =>
              setFieldValue('basicDetails.name', event.currentTarget.value)
            }
            error={
              (touched.basicDetails?.name && !!errors.basicDetails?.name) ||
              undefined
            }
            helpertext={
              touched.basicDetails?.name
                ? getTranslate(errors.basicDetails?.name || '')
                : undefined
            }
            onBlur={handleBlur}
            disabled={isSubmitting}
          />
        </div>
        <div className={classes.input}>
          {hasAccessToChangeProvider && (
            <AutocompleteWithLabel
              label={getTranslate('Provider')}
              placeholder={getTranslate('please select a provider')}
              inputSize="small"
              fullWidth
              includeInputInList
              onChange={(event, v: any) => {
                setAutoCompleteLabelForProvider(
                  v && { value: v.value, label: v.label }
                )
                setproviderCodeState(v && v.value)
              }}
              getOptionSelected={(option, value) =>
                option.value === value.value && option.label === value.label
              }
              getOptionLabel={(option) => option.label || ''}
              options={
                providers
                  ? providers?.providers?.map((pr) => ({
                      value: pr.providerCode || '',
                      label: pr.name
                    }))
                  : []
              }
              renderOption={(option) => <>{option.label}</>}
              size="small"
              value={autoCompleteLabelForProvider}
              onBlur={handleBlur}
              disabled={isSubmitting || id}
              error={touched.basicDetails?.tenantId && !providerCodeState}
              helpertext={
                touched.basicDetails?.tenantId && !providerCodeState
                  ? 'This field is required'
                  : undefined
              }
              loading={loadingForProvidersList}
              loadingText={getTranslate('Loading') + '...'}
            />
          )}
        </div>
      </div>
      <div className={classes.textEditorContainer}>
        <div className={classes.input}>
          <TextAreaWithLabel
            label={getTranslate('Description')}
            value={values.basicDetails.description
              .replace(/<\/?ul>/g, '')
              .replace(/<\/?li.*?>/g, '')}
            noHelperText
            placeholder={getTranslate('Description')}
            autoComplete="off"
            rows={9}
            onChange={(event) =>
              setFieldValue(
                'basicDetails.description',
                event.currentTarget.value
              )
            }
          />
          <div className={classes.requiredFieldContainer}>
            {!loadingForData && !!!values.basicDetails.description && (
              <Typography variant="caption" className={classes.requiredField}>
                {getTranslate('This field is required')}
              </Typography>
            )}
          </div>
        </div>
        <div className={classes.input}>
          <TextAreaWithLabel
            label={`${getTranslate('Description for')} ${nameLanguage}`}
            value={
              descriptionMultiLingual.length > 0
                ? descriptionMultiLingual[0].value
                    .replace(/<\/?ul>/g, '')
                    .replace(/<\/?li.*?>/g, '')
                : ''
            }
            noHelperText
            placeholder={`${getTranslate('Description for')} ${nameLanguage}`}
            autoComplete="off"
            rows={9}
            onChange={(event) => {
              setdescriptionMultiLingual([
                {
                  label: 'Iran, Islamic Republic of',
                  nativeLabel: 'فارسی',
                  value: event.currentTarget.value
                }
              ])
            }}
            rightHeader={
              <CountrySelectorForHeader
                selectedCountry={selectedCountryForName}
                onChange={(item: CountryType) =>
                  setselectedCountryForName(item)
                }
              />
            }
          />
          <div className={classes.requiredFieldContainer}>
            {!loadingForData &&
              !!!(
                descriptionMultiLingual.length > 0 &&
                descriptionMultiLingual[0].value
              ) && (
                <Typography variant="caption" className={classes.requiredField}>
                  {getTranslate('This field is required')}
                </Typography>
              )}
          </div>
        </div>
      </div>
    </div>
  )
}
export default PoliciesFormInputBox
